import React from 'react';
import * as mui from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

const JobHistoryWidget = ({ jobHistory, onClick }) => {
  // Get statistics
  const successJobs = jobHistory.filter(job => job.STATUS === 'SUCCESS').length;
  const failedJobs = jobHistory.filter(job => job.STATUS === 'FAILED').length;
  const inProgressJobs = jobHistory.filter(job => job.STATUS === 'IN_PROGRESS').length;
  const totalJobs = jobHistory.length;
  
  // Calculate success rate as a percentage
  const successRate = totalJobs > 0 ? Math.round((successJobs / totalJobs) * 100) : 0;
  
  // Show max 5 recent jobs
  const recentJobs = [...jobHistory]
    .sort((a, b) => {
      const dateA = a.RUN_DATE ? new Date(a.RUN_DATE) : new Date(0);
      const dateB = b.RUN_DATE ? new Date(b.RUN_DATE) : new Date(0);
      return dateB - dateA;
    })
    .slice(0, 5);
  
  // Prepare chart data
  const chartData = {
    labels: ['Success', 'Failed', 'In Progress'],
    datasets: [
      {
        data: [successJobs, failedJobs, inProgressJobs],
        backgroundColor: [
          '#4caf50', // success - green
          '#f44336', // failed - red
          '#2196f3', // in progress - blue
        ],
        borderWidth: 0,
      },
    ],
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 10
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const percentage = totalJobs > 0 ? Math.round((value / totalJobs) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    cutout: '70%'
  };
  
  return (
    <mui.Paper sx={{ p: 2, height: '100%' }}>
      <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <HistoryIcon sx={{ mr: 1, color: 'primary.main' }} />
        <mui.Typography variant="h6">Job Execution History</mui.Typography>
        <mui.Box sx={{ flexGrow: 1 }} />
        <mui.Button 
          size="small" 
          endIcon={<VisibilityIcon />} 
          onClick={onClick}
        >
          View All
        </mui.Button>
      </mui.Box>
      
      <mui.Divider sx={{ mb: 2 }} />
      
      <mui.Grid container spacing={2}>
        <mui.Grid item xs={6} md={5}>
          <mui.Box sx={{ position: 'relative', height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Doughnut data={chartData} options={chartOptions} />
            <mui.Box sx={{ 
              position: 'absolute', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              textAlign: 'center'
            }}>
              <mui.Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {successRate}%
              </mui.Typography>
              <mui.Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Success Rate
              </mui.Typography>
            </mui.Box>
          </mui.Box>
        </mui.Grid>
        <mui.Grid item xs={6} md={7}>
          <mui.Grid container spacing={1}>
            <mui.Grid item xs={12}>
              <mui.Paper 
                elevation={0}
                sx={{ 
                  p: 1, 
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'success.lighter',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'success.light'
                }}
              >
                <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                <mui.Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Success: {successJobs} jobs
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.Paper 
                elevation={0}
                sx={{ 
                  p: 1, 
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'error.lighter',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'error.light'
                }}
              >
                <ErrorIcon color="error" fontSize="small" sx={{ mr: 1 }} />
                <mui.Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Failed: {failedJobs} jobs
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            <mui.Grid item xs={12}>
              <mui.Paper 
                elevation={0}
                sx={{ 
                  p: 1, 
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'primary.lighter',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.light'
                }}
              >
                <AccessTimeIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                <mui.Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  In Progress: {inProgressJobs} jobs
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
          </mui.Grid>
        </mui.Grid>
      </mui.Grid>
      
      <mui.Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'medium' }}>
        Recent Job Executions
      </mui.Typography>
      
      {recentJobs.length > 0 ? (
        <mui.List dense disablePadding>
          {recentJobs.map((job, index) => (
            <mui.ListItem 
              key={index}
              disablePadding
              sx={{
                py: 0.5,
                borderBottom: index < recentJobs.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider'
              }}
            >
              <mui.ListItemIcon sx={{ minWidth: 36 }}>
                {job.STATUS === 'SUCCESS' ? (
                  <CheckCircleIcon fontSize="small" color="success" />
                ) : job.STATUS === 'FAILED' ? (
                  <ErrorIcon fontSize="small" color="error" />
                ) : (
                  <AccessTimeIcon fontSize="small" color="primary" />
                )}
              </mui.ListItemIcon>
              <mui.ListItemText
                primary={job.JOB_NAME}
                secondary={new Date(job.RUN_DATE).toLocaleString()}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <mui.Chip 
                label={job.JOB_TYPE ? job.JOB_TYPE.replace(/_/g, ' ') : 'Unknown'} 
                size="small" 
                color={
                  job.JOB_TYPE === 'access_termination' ? 'primary' :
                  job.JOB_TYPE === 'admin_group_scan' ? 'secondary' :
                  job.JOB_TYPE === 'terminated_user_scan' ? 'error' :
                  'default'
                }
                variant="outlined"
              />
            </mui.ListItem>
          ))}
        </mui.List>
      ) : (
        <mui.Box sx={{ textAlign: 'center', py: 2 }}>
          <mui.Typography variant="body2" color="text.secondary">
            No job history available
          </mui.Typography>
        </mui.Box>
      )}
    </mui.Paper>
  );
};

export default JobHistoryWidget; 